import { useMemo } from 'react';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import { useStableQuery } from '../../db/useStableQuery';
import {
    timeEntryPersonResolver,
    timeEntriesOverlap,
    findTimeEntryOverlaps,
    clampTimeEntryType,
    type TimeEntryDocType,
    type TimeOverlapInput
} from '@ast-data/shared';

/**
 * The ids of every time entry that overlaps another (same person or Gerät, same
 * day) — across ALL projects, and only ever within one variant. This is the
 * post-sync flag: it catches double-bookings a device couldn't block at save
 * time because the other entry only arrived via replication.
 */
export function useTimeEntryOverlaps(): Set<string> {
    const entriesQuery = useStableQuery<TimeEntryDocType>({});
    const { results: entriesDocs } = useLiveRxQuery<TimeEntryDocType>({
        collection: 'timeEntries',
        query: entriesQuery
    });
    const entries = useMemo(
        () => entriesDocs.map((doc) => doc.toMutableJSON() as TimeEntryDocType),
        [entriesDocs]
    );

    return useMemo(() => {
        /**
         * The key is resolved against ALL entries rather than read off each one:
         * an entry written before its worker's account link was worked out
         * carries no `userId`, and keying on the entry alone would file it under
         * its NAME while the linked copy sits under `u:<id>` — two groups, never
         * compared, and a real double-booking shown as two clean rows.
         */
        const personKey = timeEntryPersonResolver(entries);
        const groups = new Map<string, TimeEntryDocType[]>();
        for (const entry of entries) {
            const key = personKey(entry);
            if (!key || !entry.date) {
                continue;
            }
            // Clamped, exactly as timeEntriesOverlap compares it: a missing or
            // unknown entryType IS a worker row, and must land in the workers'
            // group rather than a lone one of its own.
            const groupKey = `${clampTimeEntryType(entry.entryType)}__${key}__${entry.date}`;
            const group = groups.get(groupKey);
            if (group) {
                group.push(entry);
            } else {
                groups.set(groupKey, [entry]);
            }
        }

        const conflicts = new Set<string>();
        for (const group of groups.values()) {
            for (let index = 0; index < group.length; index++) {
                for (
                    let otherIndex = index + 1;
                    otherIndex < group.length;
                    otherIndex++
                ) {
                    if (timeEntriesOverlap(group[index], group[otherIndex])) {
                        conflicts.add(group[index].id);
                        conflicts.add(group[otherIndex].id);
                    }
                }
            }
        }
        return conflicts;
    }, [entries]);
}

/**
 * The entries a given one collides with — the same rule the save path blocks on
 * (`findTimeEntryOverlaps`), run live against everything on the device. Ask it
 * about a SAVED row (the grid's conflict panel) or about what is currently
 * typed into the form; because it follows the typed values, correcting the
 * times makes the warning go by itself.
 *
 * Pass `null` while there is nothing comparable yet. The candidate is excluded
 * by its own id, so an entry never conflicts with itself.
 */
export function useTimeEntryOverlapsWith(
    candidate: TimeOverlapInput | null
): TimeEntryDocType[] {
    const candidates = useMemo(
        () => (candidate ? [candidate] : []),
        [candidate]
    );
    return useTimeEntryOverlapsWithEach(candidates);
}

/**
 * The same question for SEVERAL candidates at once — what the form dialog asks
 * while more than one Mitarbeiter is picked for the same span. The hits are
 * merged into one list, which is what the conflict panel shows: every entry
 * standing in the way, whoever it belongs to.
 */
export function useTimeEntryOverlapsWithEach(
    candidates: readonly TimeOverlapInput[]
): TimeEntryDocType[] {
    const entriesQuery = useStableQuery<TimeEntryDocType>({});
    const { results: entriesDocs } = useLiveRxQuery<TimeEntryDocType>({
        collection: 'timeEntries',
        query: entriesQuery
    });
    const entries = useMemo(
        () => entriesDocs.map((doc) => doc.toMutableJSON() as TimeEntryDocType),
        [entriesDocs]
    );
    return useMemo(() => {
        const found = new Map<string, TimeEntryDocType>();
        for (const candidate of candidates) {
            for (const overlap of findTimeEntryOverlaps(candidate, entries)) {
                found.set(overlap.id, overlap);
            }
        }
        return [...found.values()];
    }, [candidates, entries]);
}
